import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useAuth } from './context'
import { IDLE_TIMEOUT_MINUTES } from './idle'
import { keepSignedIn } from './persistence'

/**
 * Re-authenticate over the screen the user was already on.
 *
 * S-D2's session-expiry behaviour, and the plan states it explicitly: the app
 * "re-auths in place rather than dumping to /login". The screen behind stays
 * mounted, so a half-filled settings form survives a session that expired while
 * it was being typed into.
 *
 * Not dismissable. Every request behind it is 401ing, so the only two ways out
 * are signing back in and signing out — and offering "close" would leave a user
 * clicking a dead interface trying to work out why.
 *
 * Two accounts cannot use this dialog, and the reason is the same for both: it
 * asks for a password, and they have none the server would accept.
 *
 *  * A **Google-only identity** (`has_password === false`) has never set one.
 *  * An account with **no email and no mobile** cannot be identified at all —
 *    §4 permits `users.email` to be null for a mobile signup, and while such an
 *    account always has a mobile in practice, the *type* allows neither.
 *
 * Both are sent to `/login`, which is where the other sign-in methods live.
 */
export function SessionExpired(): JSX.Element {
  const { user, adopt, signOut } = useAuth()
  const [password, setPassword] = useState('')

  const login = useMutation(
    (body: { email?: string; mobile?: string; password: string; persistent: boolean }) =>
      call(() => api.POST('/api/v1/auth/login', { body })),
  )

  const identifier = user?.email ?? user?.mobile ?? null
  const canReauthenticate = Boolean(user?.has_password && identifier)

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!user) return
    // The dialog has no "keep me signed in" box of its own — it is a
    // re-authentication, not a sign-in choice — so it re-states the choice this
    // browser last made rather than picking one. Assuming `true` would override
    // somebody who deliberately unticked it on a shared machine; assuming
    // `false` would sign a working user out at the next browser close because
    // their session happened to lapse once.
    const persistent = keepSignedIn()
    const result = await login.run(
      user.email
        ? { email: user.email, password, persistent }
        : { mobile: user.mobile ?? '', password, persistent },
    )
    if (result.ok) {
      setPassword('')
      adopt({ user: result.data.user, session: result.data.session })
    }
  }

  return (
    <Modal
      title="Your session expired"
      /* AUTH-65/66/68/73. Screen 11 draws this as a 24rem `.ar-card`, centred,
         its title a `<strong>` at body size — not the 34rem panel with a
         1.5rem `<h2>` the app was using. `variant="prompt"` is that shape, and
         it also moves the actions into the body (AUTH-72) so the dialog has no
         bordered footer rail across it. */
      variant="prompt"
      dismissable={false}
      onClose={() => {
        /* Deliberately inert — see the note above. */
      }}
    >
      {/* AUTH-69. The lock the design puts above the title. Decorative — the
          dialog says in words what it is — so it is `aria-hidden` and carries
          no label of its own. */}
      <span className="ar-empty__icon app-prompt__icon" aria-hidden="true">
        🔒
      </span>

      {canReauthenticate ? (
        // `method="post"` so a submit with dead JavaScript cannot put this password
        // in the query string. This one sits inside a dialog on whatever screen the
        // session expired on, so the URL it would leak into is a real application
        // route rather than `/login`. Reasoning in full on `Login.tsx`'s form.
        <form
          id="reauth"
          method="post"
          className="ar-stack"
          onSubmit={(event) => {
            void onSubmit(event)
          }}
        >
          {/* AUTH-70. Body-sm and muted, and shorter than what was here: the
              design's line says how long a session lasts and that the draft
              survived, which is the two things a reader interrupted mid-edit
              wants to know.

              **The number is ours, not the mockup's.** Screen 11 says twelve
              hours; this app's idle timeout is `IDLE_TIMEOUT_SECONDS` —
              fifteen minutes — with a fourteen-day absolute cap over it.
              Printing the drawn figure would have somebody who was away for
              twenty minutes conclude the sign-out was a fault.

              An open tab usually signs itself out before this dialog is ever
              reached (`useIdleSignOut`); it still appears for a session the
              server ended first — a laptop asleep past the timeout, a session
              revoked from the sessions panel, the absolute cap. */}
          <p className="ar-muted app-prompt__body">
            For your security we sign you out after {IDLE_TIMEOUT_MINUTES} minutes without activity.
            Anything unsaved on the page behind is still here.
          </p>
          <InlineError failure={login.state.failure} />
          {/* Kept, against AUTH-71's row and with the parity review's own
              *Out of scope — noted* list, which records this field as an app
              addition rather than a gap. The dialog opens over whatever screen
              the reader was on, with no sign-in page around it to say whose
              session lapsed — and on a shared machine "enter your password"
              with no name attached is a question you cannot answer. */}
          <TextField
            label="Signed in as"
            value={identifier ?? ''}
            readOnly
            autoComplete="username"
          />
          <TextField
            label="Password"
            type="password"
            required
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              login.reset()
            }}
          />
          {/* AUTH-72. Full width, in the body, directly under the field it
              submits — where the mockup's "Unlock" is. The label stays "Sign
              back in": it is what the button does, and the app does not have a
              lock to open. */}
          <Button tone="primary" block type="submit" busy={login.pending}>
            Sign back in
          </Button>
        </form>
      ) : (
        <>
          <p className="ar-muted app-prompt__body">
            You have been signed out for security. This account signs in without a password, so
            finish on the sign-in page.
          </p>
          <a className="ar-btn ar-btn--primary ar-btn--block" href="/login">
            Go to sign in
          </a>
        </>
      )}

      {/* AUTH-22, and AUTH-72 for where it now sits: the design puts this
          below the primary action as an `.ar-link` at body-sm, not beside it
          as a second button.

          S-D2 screen 11 labels it "Sign in as someone else", which is what
          pressing it does — the session ends and `/login` is where it lands —
          and is a better description than "Sign out" for a dialog whose other
          button is "sign back in as you". Still a `<button>` rather than the
          mockup's `<a>`: it performs an action (`DELETE /auth/logout`) rather
          than going somewhere, and a link that logs you out is a link that
          browsers will prefetch. `.app-link-button` is that pattern with a
          focus ring. */}
      <button
        type="button"
        className="app-link-button app-prompt__body"
        onClick={() => {
          void signOut()
        }}
      >
        Sign in as someone else
      </button>
    </Modal>
  )
}
